import { Trans, useTranslation } from "react-i18next";

import type { SprintMission } from "@/campaigns/modules/sprint-mission/types";
import {
  hasWon,
  holdsPayoutUntilEnd,
} from "@/campaigns/modules/sprint-mission/types";
import {
  parseEndDate,
  useTimeRemaining,
} from "@/components/campaign-card/campaign-time-remaining";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

import { useSprintMissionPhase } from "./use-sprint-mission-phase";
import { PrizeText } from "@/components/prize-text";

/**
 * The payout strip under the won card (Figma `payout`, 6032:2282): "You will get your Prize in
 * 17d 4h 32m". Rendered only when the pool pays out at campaign end — an operator setting on the
 * wire as `payout_timing` — so an instant-payout winner, who already has the prize, never sees
 * a countdown to nothing. Gone once the run is finished: the prize has landed by then and the
 * status card says so. Its own component (design decision) so the prize card stays untouched.
 */
export const SprintMissionPayoutStrip = ({
  sprintMission,
  className,
}: {
  sprintMission: SprintMission;
  className?: string;
}) => {
  const { t } = useTranslation();
  const phase = useSprintMissionPhase(sprintMission);
  const show =
    phase === "won" &&
    hasWon(sprintMission) &&
    holdsPayoutUntilEnd(sprintMission);
  // Unconditional: hooks first, the early return after. `active` keeps the ticker off otherwise.
  const remaining = useTimeRemaining(
    parseEndDate(sprintMission.campaign.end),
    t,
    show
  );

  if (!show) return null;

  return (
    // The shared card (no hardcoded background), holding the one line.
    <Card
      inset="none"
      data-testid="sprint-mission-payout"
      className={cn("px-3.5 py-2.5", className)}
    >
      <p className="flex w-full flex-wrap items-center gap-x-1 font-body text-[12px] leading-4 text-foreground lg:text-[13px] lg:leading-[18px]">
        <span>
          <Trans
            i18nKey="sprintMission.payoutIn"
            defaults="You will get your <prize>Prize</prize> in"
            components={{ prize: <PrizeText size="inherit" weight="bold" /> }}
          />
        </span>
        <span className="font-heading font-bold tabular-nums">{remaining}</span>
      </p>
    </Card>
  );
};
